import { hashKey } from "@tanstack/react-query";
import { useEffect, useEffectEvent } from "react";
import { useFirebaseUser } from "../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../useSellarCurrentBusiness.shared";
import { ApiV2ContextOptions, isApiV2ContextReady } from "./apiV2Request";

/**
 * For idempotent mutations caused by displaying a view, such as marking it read.
 * Re-runs on mount, context/data changes, or becoming enabled. This does not
 * guarantee exactly-once execution or replace guards on manually called mutations.
 * Variables must be JSON-serializable, like query keys; equal values do not re-run.
 */
export const useApiMutationOnView = <TVariables>({
  mutate,
  variables,
  enabled = true,
  ...contextOptions
}: ApiV2ContextOptions & {
  mutate: (variables: TVariables) => void;
  variables: TVariables;
  enabled?: boolean;
}) => {
  const { user } = useFirebaseUser();
  const { business } = useSellarCurrentBusiness();
  const isEnabled =
    enabled &&
    isApiV2ContextReady({ user, business, overrideOptions: contextOptions });
  const variablesKey = hashKey([variables]);

  // The view event uses the latest mutation callback/data. Their identities can
  // change on re-render; the serialized data and context below define a new view.
  const onView = useEffectEvent(() => mutate(variables));

  useEffect(() => {
    if (isEnabled) onView();
  }, [isEnabled, variablesKey, user?.uid, business?.id]);
};
